// AI-GENERATED: Shared utility functions for Autocomplete components

/**
 * Common getOptionSelected function for Material-UI Autocomplete components
 * Handles both object and string comparisons for roles, units, and other entities
 * Enhanced to handle string/number ID mismatches
 * 
 * @param {Object} option - The option from the autocomplete list
 * @param {Object|string} value - The current selected value
 * @returns {boolean} - Whether the option matches the value
 */
export const getOptionSelected = (option, value) => {
    if (!option || !value) return false;
    
    // Handle object comparison for roles/units/entities with id property
    if (typeof value === 'object' && value.id && option.id) {
        // Convert both IDs to strings for comparison to handle string/number mismatch
        return String(option.id) === String(value.id);
    }
    
    // Handle string comparison (fallback)
    return option.description === value;
};

/**
 * Flexible getOptionSelected function for Material-UI Autocomplete components
 * Allows custom field specification for comparison
 * Enhanced to handle string/number field mismatches
 * 
 * @param {string} fieldName - The field name to use for comparison (e.g., 'id', 'racfID')
 * @returns {function} A getOptionSelected function that compares by the specified field
 */
export const getOptionSelectedByField = (fieldName) => {
    return (option, value) => {
        if (!option || !value) return false;
        
        if (typeof value === 'object' && value[fieldName] && option[fieldName]) {
            // Convert both field values to strings for comparison to handle string/number mismatch
            return String(option[fieldName]) === String(value[fieldName]);
        }
        
        // Handle string comparison (fallback)
        return option.description === value;
    };
};

/**
 * Conditional getOptionSelected function for components that need different field comparisons
 * based on a type or condition
 * 
 * @param {string} condition - The condition to check (e.g., type value)
 * @param {string} conditionValue - The value to compare the condition against (e.g., 'sName')
 * @param {string} primaryField - The field to use when condition matches
 * @param {string} fallbackField - The field to use when condition doesn't match
 * @returns {function} A getOptionSelected function that uses conditional field comparison
 */
export const getOptionSelectedConditional = (condition, conditionValue, primaryField, fallbackField) => {
    return (option, value) => {
        if (!option || !value) return false;
        
        const fieldToUse = condition === conditionValue ? primaryField : fallbackField;
        
        if (typeof value === 'object' && value[fieldToUse] && option[fieldToUse]) {
            return option[fieldToUse] === value[fieldToUse];
        }
        
        return option.description === value;
    };
};
